import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Users, Mail, Phone, Building, User, Loader2, Eye, Trash2 } from 'lucide-react';
import { CUILookupInput } from '@/components/CUILookupInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_type: 'persoana_fizica', name: '', cnp_cui: '', id_document: '', administrator: '', email: '', phone: '', address: '', notes: '' });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/clients', {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true,
        params: search ? { search } : {}
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea clienților');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.cnp_cui || !form.email || !form.phone) {
      toast.error('Completați câmpurile obligatorii');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(API + '/admin/clients', form, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success('Client adăugat');
      setDialogOpen(false);
      setForm({ client_type: 'persoana_fizica', name: '', cnp_cui: '', id_document: '', administrator: '', email: '', phone: '', address: '', notes: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (clientId, clientName) => {
    if (!confirm(`Sigur doriți să ștergeți clientul "${clientName}"? Această acțiune va dezactiva clientul.`)) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(API + '/admin/clients/' + clientId, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      toast.success('Client șters');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Clienți</h1>
          <p className="text-slate-500">Gestionează clienții</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" />Client nou
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Adaugă client nou</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Tip client</Label>
                <Select value={form.client_type} onValueChange={(v) => setForm({...form, client_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="persoana_fizica">Persoană fizică</SelectItem>
                    <SelectItem value="persoana_juridica">Persoană juridică</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{form.client_type === 'persoana_fizica' ? 'Nume complet' : 'Denumire firmă'} *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><Label>{form.client_type === 'persoana_fizica' ? 'CNP' : 'CUI'} *</Label>
                {form.client_type === 'persoana_juridica' ? (
                  <CUILookupInput
                    value={form.cnp_cui}
                    onChange={(e) => setForm({...form, cnp_cui: e.target.value})}
                    onCompanyFound={(company) => setForm(prev => ({
                      ...prev,
                      name: company.name || prev.name,
                      cnp_cui: company.cui || prev.cnp_cui,
                      address: company.address || prev.address,
                      id_document: company.registration_number || prev.id_document
                    }))}
                  />
                ) : (
                  <Input value={form.cnp_cui} onChange={(e) => setForm({...form, cnp_cui: e.target.value})} />
                )}
              </div>
              <div><Label>{form.client_type === 'persoana_fizica' ? 'Serie Număr CI' : 'Nr. Reg. Comerțului'}</Label><Input data-testid="client-id-document" value={form.id_document} onChange={(e) => setForm({...form, id_document: e.target.value})} placeholder={form.client_type === 'persoana_fizica' ? 'ex: RX 123456' : 'ex: J40/1234/2020'} /></div>
              {form.client_type === 'persoana_juridica' && (
                <div><Label>Administrator</Label><Input value={form.administrator} onChange={(e) => setForm({...form, administrator: e.target.value})} placeholder="Nume administrator" data-testid="client-administrator" /></div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
                <div><Label>Telefon *</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              </div>
              <div><Label>Adresa</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Strada, număr, oraș, județ" /></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{saving ? 'Se salvează...' : 'Salvează'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setLoading(true); fetchClients(); }} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Caută..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Button type="submit" variant="outline">Caută</Button>
      </form>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : clients.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Users className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">Niciun client</p></CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {clients.map((client) => (
            <Card key={client.client_id} className="bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${client.client_type === 'persoana_juridica' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                      {client.client_type === 'persoana_juridica' ? <Building className="h-6 w-6 text-purple-600" /> : <User className="h-6 w-6 text-blue-600" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{client.name}</p>
                        <Badge variant="secondary" className="text-xs">{client.client_type === 'persoana_juridica' ? 'PJ' : 'PF'}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>
                        {client.id_document && <span className="text-slate-400">{client.client_type === 'persoana_fizica' ? 'CI' : 'Reg.'}: {client.id_document}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/clients/${client.client_id}`}>
                      <Button variant="outline" size="sm"><Eye className="h-4 w-4 mr-2" />Detalii</Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => deleteClient(client.client_id, client.name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
